// Direction B "Ink Header" assistant turn. Per spec §3.5 + §7.9 + handoff:
//   - 28px-wide avatar slot on the left (pink "m." mark on the FIRST bubble
//     of the group; later bubbles in the same group leave the slot empty so
//     all bubbles in a group share the same left edge).
//   - Bubbles stack with 10px gap.
//   - Optional meta line below ("MIKLA · REPLIED IN 47s") rendered by
//     MessageList, not here, since meta belongs to the group not the turn.
//
// REGRESSION GUARDRAIL: Re-adding HandoffCard or EventAvailabilityCard requires
// deleting the corresponding null branches in the renderer below AND the
// negative assertions in src/__tests__/message-renderer.test.tsx. The dual-touch
// is intentional — it prevents accidental card re-introduction.
//
// The 3 cards we DO render (Booking / Reschedule / Cancellation) are the
// "moment-of-truth" cards from the Q1 + Q5 reversal — calendar state changes
// the user must visually confirm. All other tools render null at output-available;
// the bot's own prose carries the user-visible signal.

import { TextBubble } from './TextBubble'
import { TypingDots } from './TypingDots'
import { BookingConfirmationCard } from './cards/BookingConfirmationCard'
import { RescheduleCard } from './cards/RescheduleCard'
import { CancellationCard } from './cards/CancellationCard'
import { MiklaAvatar } from './MiklaAvatar'
import { useVendorInfo } from '../context/VendorInfoContext'

export interface AssistantPart {
  type: string
  text?: string
  toolName?: string
  toolCallId?: string
  state?: string
  input?: Record<string, unknown>
  output?: unknown
}

export interface AssistantTurnProps {
  id: string
  parts: AssistantPart[]
}

export function AssistantTurn({ id, parts }: AssistantTurnProps) {
  // Only the FIRST in-flight tool in a turn shows the inline TypingDots —
  // multiple parallel tools (e.g. get_appointment_config + check_calendar via
  // asyncio.gather) would otherwise stack 2-3 visually-identical sets.
  const firstStreamingToolIdx = parts.findIndex(
    (p) =>
      p.type === 'dynamic-tool' &&
      (p.state === 'input-streaming' || p.state === 'input-available'),
  )

  // Render every assistant part as its own row so each lines up against the
  // 28px avatar slot. The avatar is shown only on the FIRST text bubble; tool
  // outputs (cards) and inline TypingDots use an empty slot.
  let firstBubbleRendered = false
  return (
    <div data-testid="assistant-turn" className="flex flex-col gap-2.5">
      {parts.map((p, i) => {
        if (p.type === 'text') {
          const showAvatar = !firstBubbleRendered
          firstBubbleRendered = true
          return (
            <Row key={`${id}-text-${i}`} avatar={showAvatar}>
              <TextBubble role="assistant">{p.text || ''}</TextBubble>
            </Row>
          )
        }
        if (p.type === 'dynamic-tool') {
          if (p.state === 'input-streaming' || p.state === 'input-available') {
            if (i !== firstStreamingToolIdx) return null
            return (
              <Row key={p.toolCallId || `${id}-tool-${i}`}>
                <TypingDots variant="inline" />
              </Row>
            )
          }
          if (p.state === 'output-available') {
            if (p.toolName === 'book_appointment') {
              return (
                <Row key={p.toolCallId || `${id}-book-${i}`}>
                  <BookingConfirmationCard input={p.input || {}} output={p.output} />
                </Row>
              )
            }
            if (p.toolName === 'reschedule_appointment') {
              return (
                <Row key={p.toolCallId || `${id}-resch-${i}`}>
                  <RescheduleCard input={p.input || {}} output={p.output} />
                </Row>
              )
            }
            if (p.toolName === 'cancel_appointment') {
              return (
                <Row key={p.toolCallId || `${id}-cancel-${i}`}>
                  <CancellationCard input={p.input || {}} output={p.output} />
                </Row>
              )
            }
            // All other tools render null — the bot's prose carries the signal.
            return null
          }
          return null
        }
        return null
      })}
    </div>
  )
}

interface RowProps {
  avatar?: boolean
  children: React.ReactNode
}

function Row({ avatar = false, children }: RowProps) {
  const info = useVendorInfo()
  return (
    <div className="flex items-end gap-2 max-w-full">
      <div className="w-7 flex-shrink-0 flex items-end">
        {avatar && <MiklaAvatar size="sm" logoUrl={info.logo_url} />}
      </div>
      {children}
    </div>
  )
}
